import { format } from "date-fns";

import type {
  IncidentDetailResponseDto,
  IncidentTimelineEntryResponseDto,
  IncidentUserSummaryDto,
} from "@/generated/model";

/**
 * Audit-trail timeline model for the incident detail view (NAU-219).
 *
 * The in-app history must give a manager the complete picture of what happened
 * and who acted — every incident lifecycle event interleaved with the
 * temperature readings, in chronological order, with date/time/user stamps —
 * without opening the PDF export.
 *
 * This module turns an `IncidentDetailResponseDto` into a flat, time-ordered
 * list of `IncidentTimelineItem`s. It is pure (no React) so it can be unit
 * tested directly; `IncidentTimeline.tsx` only renders the result.
 */

export type IncidentTimelineUser = Pick<
  IncidentUserSummaryDto,
  "firstName" | "lastName" | "email"
>;

export type IncidentTimelineItem =
  | {
      kind: "reading";
      at: string;
      entry: IncidentTimelineEntryResponseDto;
      isTriggering: boolean;
    }
  | { kind: "alert-triggered"; at: string }
  | {
      kind: "follow-up-created";
      at: string;
      description: string;
      by: IncidentTimelineUser | null;
    }
  | {
      kind: "follow-up-completed";
      at: string;
      description: string;
      by: IncidentTimelineUser | null;
      notes: string | null;
    }
  | {
      kind: "follow-up-cancelled";
      at: string;
      description: string;
      by: IncidentTimelineUser | null;
    }
  | {
      kind: "signed-off";
      at: string;
      by: IncidentTimelineUser | null;
      notes: string | null;
    };

/** "First Last", falling back to email, then a neutral placeholder. */
export function formatIncidentUser(user?: IncidentTimelineUser | null): string {
  if (!user) return "Unknown user";
  return (
    [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email
  );
}

/**
 * Secondary ordering rank, applied only when two items share the same
 * timestamp. Keeps the narrative readable: a reading is shown before the alert
 * it triggered, follow-up events sit in the middle, and a sign-off is shown
 * last among same-instant events.
 */
const TIE_BREAK_RANK: Record<IncidentTimelineItem["kind"], number> = {
  reading: 0,
  "alert-triggered": 1,
  "follow-up-created": 2,
  "follow-up-completed": 2,
  "follow-up-cancelled": 2,
  "signed-off": 3,
};

function tieBreakRank(kind: IncidentTimelineItem["kind"]): number {
  return TIE_BREAK_RANK[kind];
}

/**
 * Build the chronological audit trail. Items with no timestamp (e.g. an open
 * incident has no sign-off, a non-cancelled task has no cancellation) are
 * simply omitted.
 */
export function buildIncidentTimeline(
  incident: IncidentDetailResponseDto,
): IncidentTimelineItem[] {
  const items: IncidentTimelineItem[] = [];

  // Readings (already chronological from the API, but we re-sort the whole set
  // below so they interleave correctly with lifecycle events).
  for (const entry of incident.entries) {
    items.push({
      kind: "reading",
      at: entry.recordedAt,
      entry,
      isTriggering: entry.id === incident.firstReading.id,
    });
  }

  // The out-of-range alert that opened the incident. We anchor it to the
  // triggering reading's timestamp (the true moment it fired) rather than
  // `openedAt`, which the server sets asynchronously after the reading lands.
  items.push({
    kind: "alert-triggered",
    at: incident.firstReading.recordedAt,
  });

  // Follow-up task lifecycle: created, plus completed / cancelled when present.
  for (const action of incident.followUpActions) {
    items.push({
      kind: "follow-up-created",
      at: action.createdAt,
      description: action.description,
      by: action.createdBy ?? null,
    });
    if (action.completedAt) {
      items.push({
        kind: "follow-up-completed",
        at: action.completedAt,
        description: action.description,
        by: action.completedBy ?? null,
        notes: action.completionNotes ?? null,
      });
    }
    if (action.cancelledAt) {
      items.push({
        kind: "follow-up-cancelled",
        at: action.cancelledAt,
        description: action.description,
        by: action.cancelledBy ?? null,
      });
    }
  }

  // Sign-off (only once resolved). The remedial-action note lives here, per the
  // single-`resolutionNotes`-at-sign-off model.
  if (incident.resolvedAt) {
    items.push({
      kind: "signed-off",
      at: incident.resolvedAt,
      by: incident.resolvedBy ?? null,
      notes: incident.resolutionNotes ?? null,
    });
  }

  return items.sort((a, b) => {
    const diff = new Date(a.at).getTime() - new Date(b.at).getTime();
    if (diff !== 0) return diff;
    return tieBreakRank(a.kind) - tieBreakRank(b.kind);
  });
}

export interface IncidentTimelineDay {
  /** `yyyy-MM-dd` key for the calendar day. */
  key: string;
  /** A Date within the day, for rendering the header. */
  date: Date;
  items: IncidentTimelineItem[];
}

/**
 * Group a (already chronological) timeline into calendar days so the view can
 * render a prominent date header once per day and a bare time per event —
 * making the sequence far easier to scan than repeating the full date on every
 * row.
 */
export function groupIncidentTimelineByDay(
  items: IncidentTimelineItem[],
): IncidentTimelineDay[] {
  const days: IncidentTimelineDay[] = [];
  for (const item of items) {
    const date = new Date(item.at);
    const key = format(date, "yyyy-MM-dd");
    const current = days[days.length - 1];
    if (current && current.key === key) {
      current.items.push(item);
    } else {
      days.push({ key, date, items: [item] });
    }
  }
  return days;
}
